import { Alert, Flex, Modal } from 'antd';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';

export const LiveMapScreen = styled(Modal)`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    top: 0;
    margin: 0;
    max-width: 100vw;
    padding-bottom: 0;
    height: 100vh;

    .ant-modal-content {
      height: 100vh;
      border-radius: 0;
      display: flex;
      flex-direction: column;
      padding: ${kcc.size.S500}px;

      .ant-modal-header {
        padding: 0;
        margin-bottom: ${kcc.spacing.LG};
      }

      .ant-modal-body {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        padding: 0;
      }

      .ant-modal-footer {
        margin-top: ${kcc.spacing.MD};
        padding: 0;
      }
    }
  `}
`;

export const Header = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
`;

export const ModalHeader = styled(Header)`
  align-items: center;
`;

export const Footer = styled(Flex)`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    flex-direction: column;
    gap: ${kcc.spacing.MD};
  `}
`;

export const ButtonGroupContainer = styled(Flex)`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    justify-content: center;
    align-items: center;
    gap: ${kcc.spacing.BASE};
  `}
`;

export const Body = styled(Flex)`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    width: 100%;
    height: 100%;
    align-items: stretch;
    gap: ${kcc.spacing.LG};
  `}
`;

export const LeftSide = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
`;

// Holds the width of the routine timeline, so the column is as wide while it loads as once it is shown.
export const RightSide = styled.div`
  width: 350px;
  max-width: 100%;
  height: 100%;
  flex-shrink: 0;
`;

export const RightSideLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const HeaderTitleContainer = styled(Flex)`
  flex-direction: column;
`;

export const MachineName = styled.p`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    font-size: ${kcc.typography.FONT_SIZE.XL}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.REGULAR};
    line-height: ${kcc.typography.LINE_HEIGHT.BASE};
    color: ${kcc.colors.TEXT};
    margin: 0;
  `}
`;

export const ScheduleName = styled.p`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    font-size: ${kcc.typography.FONT_SIZE.HEADING_3}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
    line-height: ${kcc.typography.LINE_HEIGHT.HEADING_3};
    color: ${kcc.colors.TEXT};
    margin: 0;
  `}
`;

export const ButtonIconContainer = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    width: ${kcc.size.S500}px;
    height: ${kcc.size.S500}px;
  `}
`;

export const IconContainer = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    width: ${kcc.size.S300}px;
    height: ${kcc.size.S300}px;
    cursor: pointer;
  `}
`;

export const ModalCloseButton = styled(KccButton).attrs({
  type: 'link',
  icon: (
    <IconContainer>
      <SvgIcon name="close" />
    </IconContainer>
  ),
})`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    &.ant-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: ${kcc.size.S300}px;
      min-width: ${kcc.size.S300}px;
      height: ${kcc.size.S300}px;
      padding: 0;
    }
  `}
`;

export const CloseButtonIcon = styled(KccButton).attrs({
  type: 'link',
  icon: (
    <ButtonIconContainer>
      <SvgIcon name="close" />
    </ButtonIconContainer>
  ),
})``;

export const FooterButtonIcon = styled.div`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${kcc.size.S250}px;
    height: ${kcc.size.S250}px;
  `}
`;

export const ErrorAlertMessage = styled(Alert).attrs<{ $isClosable?: boolean }>(({ $isClosable = true }) => ({
  type: 'error',
  showIcon: true,
  closable: $isClosable
    ? {
        closeIcon: (
          <IconContainer>
            <SvgIcon name="close" />
          </IconContainer>
        ),
      }
    : false,
  icon: (
    <IconContainer>
      <SvgIcon name="warningInformationCritical" />
    </IconContainer>
  ),
}))<{ $isClosable?: boolean }>`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    font-size: ${kcc.typography.FONT_SIZE.SM}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.REGULAR};
    line-height: ${kcc.typography.LINE_HEIGHT.BASE};
    color: ${kcc.colors.TEXT};
    padding: ${kcc.size.S100}px ${kcc.size.S150}px;
    border: none;

    .ant-alert-message {
      text-align: start;
    }
  `}
`;

export const StyledModalMessage = styled(Modal)`
  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    .ant-modal-content {
      .ant-modal-header {
        padding: 0;
        line-height: ${kcc.typography.LINE_HEIGHT.HEADING_3};
      }

      .ant-modal-body {
        line-height: ${kcc.typography.LINE_HEIGHT.SM};
      }
    }
  `}
`;
